import { sestavVerdikt } from './vysledovkaNalezy';
import PacingBar from './PacingBar';
import { ACCENT, TEAL } from '../../lib/uiTokens';

/**
 * Verdikt Výsledovky — jedna věta, která odpovídá na otázku, kvůli které se
 * modul otevírá: kam měsíc míří a kdo za to. Do 3.480.0 tu nebylo nic a odpověď
 * si musel člověk složit z tempa v třiceti řádcích tabulky.
 *
 * Dvě hustoty ze stejných dat (`varianta`):
 *  - `pruh`  — desktop, nad tabulkou: věta vlevo, plán a zbývající dny vpravo
 *  - `hero`  — mobil, první věc na obrazovce: velká EBITDA, pruh k plánu, věta
 *
 * Barvu nese jen odchylka od plánu (zelená/červená) a levá linka karty. Plochy
 * zůstávají neutrální — akcent sekce smí být linka nebo text, ne podklad.
 */
export default function VysledovkaVerdikt({ data, rezim, cardStyle, isDark, varianta = 'pruh' }) {
    const v = sestavVerdikt(data, rezim);
    if (!v) return null;

    const Delta = () => (v.delta
        ? <span style={{ color: v.delta.barva, fontWeight: 700 }}>{v.delta.text}</span>
        : null);

    const Proc = () => {
        if (!v.proc) return null;
        return (
            <>
                {v.proc.pred}{' '}
                <span style={{ color: v.proc.trzby.barva, fontWeight: 600 }}>{v.proc.trzby.text}</span>
                {v.proc.mezi}
                {v.proc.tahne && (
                    <> <span style={{ color: v.proc.tahne.barva, fontWeight: 600 }}>{v.proc.tahne.text}</span>.</>
                )}
            </>
        );
    };

    if (varianta === 'hero') {
        // Mobil: číslo velké, pruh k plánu pod ním, věta až nakonec. Pacing bar
        // bere projekci proti celému plánu (značka vpravo), zaúčtováno proti tempu.
        const souhrn = data.souhrn || {};
        const jeProj = rezim === 'projekce';
        const ebitdaPlan = souhrn.ebitda_plan;
        const tempo = ebitdaPlan ? (jeProj ? souhrn.ebitda_projekce : souhrn.ebitda) / ebitdaPlan : null;
        return (
            <div className="rounded-2xl p-4"
                style={{ ...cardStyle, borderLeft: `3px solid ${v.barva || ACCENT}` }}>
                <div className="text-[9.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: 'var(--text-secondary)' }}>
                    EBITDA · {rezim === 'projekce' ? 'projekce do konce měsíce' : 'zaúčtováno'}
                </div>
                <div className="flex items-baseline gap-2 mt-1.5">
                    <span className="text-[30px] font-extrabold tabular-nums leading-none">{v.ebitda}</span>
                    {v.pct && <span className="text-[15px] font-semibold" style={{ color: 'var(--text-secondary)' }}>· {v.pct}</span>}
                </div>
                {ebitdaPlan != null && (
                    <div className="mt-3">
                        <PacingBar tempo={tempo} podil={rezim === 'projekce' ? 1 : (data.pacing?.podil ?? 0)}
                            color={ACCENT} isDark={isDark} jeVyhoda />
                        <div className="flex items-center justify-between text-[11px] mt-1.5" style={{ color: 'var(--text-secondary)' }}>
                            <span>plán {v.plan}</span>
                            <Delta />
                        </div>
                    </div>
                )}
                {v.proc && (
                    <div className="text-[12px] leading-snug mt-2.5 pt-2.5"
                        style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border-default)' }}>
                        <Proc />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="rounded-2xl px-4 py-3 flex items-center gap-4"
            style={{ ...cardStyle, borderLeft: `3px solid ${v.barva || ACCENT}` }}>
            <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold leading-snug">
                    {v.uvod} <b className="font-extrabold tabular-nums">{v.ebitda}</b>
                    {v.pct && <> ({v.pct})</>}
                    {v.delta && <> — <Delta /></>}.
                </div>
                {v.proc && (
                    <div className="text-[12.5px] mt-1" style={{ color: 'var(--text-secondary)' }}><Proc /></div>
                )}
            </div>
            <div className="flex-shrink-0 text-right">
                {v.zbyva && (
                    <div className="text-[9.5px] font-bold uppercase" style={{ letterSpacing: '0.1em', color: TEAL }}>{v.zbyva}</div>
                )}
                {v.plan && (
                    <div className="text-[12px] mt-0.5 tabular-nums" style={{ color: 'var(--text-secondary)' }}>plán {v.plan}</div>
                )}
            </div>
        </div>
    );
}
